import React from 'react'

// Loader built from the QCR logo mark (design handoff "Loading Gauge").
//   variant="draw"     1a - arc draws itself, arrowhead snaps on, hand winds back (page / case loads)
//   variant="progress" 1c - arc fills to `value` (0-100), hand follows the tip, arrowhead lights at 100
// size: 'sm' (18, for buttons/inline) | 'md' (40) | 'lg' (112) | number
// tone: 'light' (cream/white) | 'navy' (on navy) | 'onBrass' (inside a brass button)
const ARC = 'M4.4 12a7.6 7.6 0 1 0 2.6-5.75';
const HEAD = 'M3.5 4.6v4.2h4.2';
const HAND = 'M12 8.1V12l-2.7 2.1';
const SIZES = { sm: 18, md: 40, lg: 112 };
const TONES = {
  light: { track: 'rgba(20,33,61,.10)', arc: '#8a6231', hand: '#14213d', head: '#8a6231' },
  navy: { track: 'rgba(247,244,237,.14)', arc: '#c9a26a', hand: '#f7f4ed', head: '#c9a26a' },
  onBrass: { track: 'rgba(255,253,248,.30)', arc: '#fffdf8', hand: '#fffdf8', head: null },
};
const ROUND = { strokeLinecap: 'round', strokeLinejoin: 'round' };

function QcrLoader({ variant = 'draw', size = 'md', tone = 'light', value = 0, label, className = '' }) {
  const px = typeof size === 'number' ? size : (SIZES[size] || SIZES.md);
  const small = px <= 24;
  const sw = small ? 2.4 : 2.1;
  const t = TONES[tone] || TONES.light;
  const showHead = !small && !!t.head;

  if (variant === 'progress') {
    const p = Math.max(0, Math.min(100, Number(value) || 0));
    return (
      <svg width={px} height={px} viewBox="0 0 24 24" fill="none" className={`flex-none overflow-visible ${className}`}
        role="progressbar" aria-valuenow={Math.round(p)} aria-valuemin={0} aria-valuemax={100} aria-label={label || 'Loading'}>
        <path d={ARC} stroke={t.track} strokeWidth={sw} {...ROUND} />
        <path d={ARC} pathLength="100" stroke={t.arc} strokeWidth={sw} {...ROUND}
          strokeDasharray={`${p} 100`} className="motion-safe:[transition:stroke-dasharray_.25s_ease]" />
        {showHead && <path d={HEAD} stroke={p >= 100 ? t.head : t.track} strokeWidth={sw} {...ROUND} />}
        <path d={HAND} stroke={t.hand} strokeWidth={sw} {...ROUND}
          className="[transform-box:view-box] [transform-origin:12px_12px] motion-safe:[transition:transform_.25s_ease]"
          style={{ transform: `rotate(${-90 - 311 * p / 100}deg)` }} />
      </svg>
    );
  }

  // 1a - draws itself (reduced motion: static full logo)
  return (
    <svg width={px} height={px} viewBox="0 0 24 24" fill="none" className={`flex-none overflow-visible ${className}`}
      role="status" aria-live="polite" aria-label={label || 'Loading'}>
      <path d={ARC} stroke={t.track} strokeWidth={sw} {...ROUND} />
      <path d={ARC} pathLength="100" strokeDasharray="100 100" stroke={t.arc} strokeWidth={sw} {...ROUND}
        className="animate-q-draw motion-reduce:animate-none" />
      {showHead &&
        <path d={HEAD} stroke={t.head} strokeWidth={sw} {...ROUND}
          className="[transform-box:view-box] [transform-origin:3.5px_8.8px] animate-q-head motion-reduce:animate-none" />}
      <path d={HAND} stroke={t.hand} strokeWidth={sw} {...ROUND}
        className="[transform-box:view-box] [transform-origin:12px_12px] animate-q-wind motion-reduce:animate-none" />
    </svg>
  );
}

// Caption under the large loader (mono, uppercase, gentle pulse)
export function QcrLoaderCaption({ children, className = '' }) {
  return (
    <span className={`font-plexmono font-medium text-[12px] leading-none tracking-[.1em] uppercase text-navy/[.55] animate-q-fade motion-reduce:animate-none ${className}`}>
      {children}
    </span>
  );
}

export default QcrLoader
